/**
 * Purchase Order API Service Layer
 * Handles all API calls for Purchase Orders
 */

import { api } from '../api/client';

class PurchaseOrderService {
  constructor() {
    // FIXED: Correct base URL (was /api/v1/finance/purchase-orders)
    this.baseURL = '/api/purchase-orders';
  }

  // Get tenant ID from localStorage or default
  getTenantId() {
    return localStorage.getItem('tenant_id') || 'tenant-123';
  }

  // Helper method to make API calls
  async apiCall(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const method = options.method || 'GET';
    
    // Always include X-Tenant-ID header
    const headers = {
      'X-Tenant-ID': this.getTenantId(),
      ...options.headers
    };
    
    try {
      let response;
      
      if (method === 'GET') {
        response = await api.get(url, { headers });
      } else if (method === 'POST') {
        const body = options.body ? JSON.parse(options.body) : null;
        response = await api.post(url, body, { headers });
      } else if (method === 'PUT') {
        const body = options.body ? JSON.parse(options.body) : null;
        response = await api.put(url, body, { headers });
      } else if (method === 'DELETE') {
        response = await api.delete(url, { headers });
      }

      // api client already returns data directly
      return response;
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // List Purchase Orders with filtering and pagination
  async listPurchaseOrders(params = {}) {
    const searchParams = new URLSearchParams();
    
    if (params.status) searchParams.append('status', params.status);
    if (params.supplier) searchParams.append('supplier', params.supplier);
    if (params.page) searchParams.append('page', params.page);
    if (params.page_size) searchParams.append('page_size', params.page_size);

    const endpoint = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return this.apiCall(endpoint);
  }

  // Get Purchase Order details by ID
  async getPurchaseOrder(poId) {
    return this.apiCall(`/${poId}`);
  }

  // Create new Purchase Order
  async createPurchaseOrder(poData) {
    return this.apiCall('', {
      method: 'POST',
      body: JSON.stringify(poData),
    });
  }

  // Mark Purchase Order as received and trigger cost allocation
  async receivePurchaseOrder(poId, receiveData) {
    return this.apiCall(`/${poId}/receive`, {
      method: 'POST',
      body: JSON.stringify(receiveData),
    });
  }

  // Get valid status options
  getValidStatuses() {
    return [
      { value: 'all', label: 'All Statuses' },
      { value: 'draft', label: 'Draft' },
      { value: 'sent_to_supplier', label: 'Sent to Supplier' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'in_production', label: 'In Production' },
      { value: 'shipped', label: 'Shipped' },
      { value: 'in_transit', label: 'In Transit' },
      { value: 'received', label: 'Received' },
      { value: 'cancelled', label: 'Cancelled' },
    ];
  }

  // Get status colors for badges
  getStatusColor(status) {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
      'sent_to_supplier': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      'confirmed': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
      'in_production': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      'shipped': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      'in_transit': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      'received': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return colors[status] || colors['draft'];
  }

  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  // Format date
  formatDate(dateString) {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Generate unique PO number
  generatePONumber() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const time = String(now.getTime()).slice(-6);
    
    return `PO-${year}${month}${day}-${time}`;
  }
}

// Create and export a singleton instance
const purchaseOrderService = new PurchaseOrderService();
export default purchaseOrderService;

// Named exports for convenience
export const {
  listPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrder,
  receivePurchaseOrder,
  getValidStatuses,
  getStatusColor,
  formatCurrency,
  formatDate,
  generatePONumber,
} = purchaseOrderService;